import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { API_BASE } from '../../../lib/api-base';
import { apiFetch } from '../../../lib/api-client';
import { CREDITS_BALANCE_QUERY_KEY, dailyCeilingSnapshot, type DailyCeilingSnapshot } from '../../../lib/credits-refresh-after-phone';
import { useTranslation } from '../../../lib/i18n';
import { useOpenPhoneNumberStep } from '../../../lib/phone-connect';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useIsPhone } from '../../../lib/use-is-phone';
import { useItemSeenBi } from '../../../lib/use-item-seen-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import type { VerifyPhoneOffer } from '../../../lib/use-credits-widget-state';
import { closeVerifyWhatsappDialog, useVerifyWhatsappDialog } from '../../../lib/verify-whatsapp-dialog-store';
import {
  WHATSAPP_VERIFY_DONE_CLOSE_MS,
  WHATSAPP_VERIFY_INITIAL,
  WHATSAPP_VERIFY_POLL_MS,
  whatsappConnectSettled,
  whatsappVerifyModeCopy,
  whatsappVerifyPolls,
  whatsappVerifyReducer,
  whatsappVerifySettled,
  whatsappVerifyStateLink,
} from '../../../lib/whatsapp-phone-verify';
import { settingsKeys } from '../../settings/shared/settings-queries';
import { PHONE_VERIFY_PAGE } from '../../../lib/use-phone-verification';
import { Modal } from '../../Modal';
import { Spinner } from '../../Spinner';
import { Button, Card, TextButton, Typography } from '../ui';
import styles from './VerifyWhatsappDialog.module.css';

const QR_SIZE = 200;
const CREDITS_PAGE = { page_name: 'menu', origin: 'menu' } as const;
const CONNECT_PAGE = { page_name: PHONE_VERIFY_PAGE.page_name, origin: 'verify_phone' } as const;

/**
 * Which ask opened the sheet. `credits` carries the offer's numbers; `connect` (no phone on file, opened by
 * `PhoneVerifyModal`) has none to show.
 */
export type VerifyWhatsappDialogMode = { kind: 'credits'; offer: VerifyPhoneOffer } | { kind: 'connect' };

/** Mounted once at the frame; renders nothing until an offer's "Verify your phone" opens it. */
export function VerifyWhatsappDialogHost() {
  const offer = useVerifyWhatsappDialog();
  return offer ? <CreditsVerifyWhatsappDialog offer={offer} /> : null;
}

/** The offer's sheet: "Enter my number instead" goes straight to the number step, never back to this sheet. */
function CreditsVerifyWhatsappDialog({ offer }: { offer: VerifyPhoneOffer }) {
  const openNumberStep = useOpenPhoneNumberStep();
  return (
    <VerifyWhatsappDialog
      mode={{ kind: 'credits', offer }}
      onClose={closeVerifyWhatsappDialog}
      onUseNumber={() => {
        closeVerifyWhatsappDialog();
        openNumberStep();
      }}
    />
  );
}

/**
 * "Verify with WhatsApp" (contract: native-apps/shared/whatsapp-phone-verify/contract.json; rules:
 * lib/whatsapp-phone-verify.ts). The person sends Symphony one prefilled WhatsApp message carrying a code; the
 * backend verifies the SENDER's number. In `credits` mode (the daily-limit offer) the daily limit rises, which this
 * dialog notices by re-reading the credits; in `connect` mode (the first step of `PhoneVerifyModal` while no phone
 * is on file) the recipient gains the number, which it notices by re-reading the recipient.
 *
 * Written-down exception — the QR is web-only: a computer has no WhatsApp to hand the link to. On a computer the
 * QR IS the action (scan with the phone, tap Send), and opening WhatsApp Web is a small link under it: in the first
 * day all three computer users who got a code fell back to typing their number, and the one who verified was on a
 * phone (2026-11-02). On a phone (`useIsPhone`) the button opens WhatsApp directly, as on iOS and Android.
 */
export function VerifyWhatsappDialog({
  mode,
  onClose,
  onUseNumber,
}: {
  mode: VerifyWhatsappDialogMode;
  onClose: () => void;
  onUseNumber: () => void;
}) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const queryClient = useQueryClient();
  const copy = whatsappVerifyModeCopy(mode.kind);
  const page = mode.kind === 'credits' ? CREDITS_PAGE : CONNECT_PAGE;
  const { logPageClickBi, logModalActionBi } = useGenericBi();
  const seenRef = useItemSeenBi(copy.bi.sheetSeen);
  const [state, dispatch] = useReducer(whatsappVerifyReducer, WHATSAPP_VERIFY_INITIAL);

  useLoadBi('verify-phone-whatsapp-start', { isLoading: state.kind === 'loading', isError: state.kind === 'error' });

  const readCeiling = useCallback(
    (): DailyCeilingSnapshot | null => dailyCeilingSnapshot(queryClient.getQueryData(CREDITS_BALANCE_QUERY_KEY)),
    [queryClient],
  );
  // The ceiling the dialog opened on; "done" is the read moving away from it.
  const before = useRef<DailyCeilingSnapshot | null>(null);
  if (before.current === null) before.current = readCeiling();

  // Only the newest mint may land: an Open WhatsApp from expired while an older call is in flight must not
  // be answered by the older code.
  const mintSeq = useRef(0);
  const mint = useCallback(() => {
    const seq = ++mintSeq.current;
    dispatch({ type: 'mint' });
    void (async () => {
      let body: unknown = null;
      try {
        const res = await apiFetch(`${API_BASE}/api/whatsapp-phone-verify-start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
        });
        body = res.ok ? await res.json() : null;
      } catch {
        body = null;
      }
      if (seq === mintSeq.current) dispatch({ type: 'minted', body });
    })();
  }, []);

  useEffect(() => {
    mint();
    return () => {
      mintSeq.current += 1;
    };
  }, [mint]);

  // Poll the credits while a code is live, and on coming back to the tab (the person went to WhatsApp).
  const polling = whatsappVerifyPolls(state);
  useEffect(() => {
    if (!polling) return;
    let cancelled = false;
    const check = async (refetch: boolean) => {
      if (refetch) {
        try {
          await queryClient.refetchQueries({ queryKey: mode.kind === 'credits' ? CREDITS_BALANCE_QUERY_KEY : settingsKeys.recipient });
        } catch {
          // the next tick tries again
        }
      }
      if (cancelled) return;
      let settled: boolean;
      if (mode.kind === 'credits') {
        if (before.current === null) before.current = readCeiling();
        settled = whatsappVerifySettled(before.current, readCeiling());
      } else {
        settled = whatsappConnectSettled(queryClient.getQueryData(settingsKeys.recipient));
      }
      dispatch({ type: 'check', nowMs: Date.now(), settled });
    };
    const timer = setInterval(() => void check(true), WHATSAPP_VERIFY_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [polling, queryClient, readCeiling, mode.kind]);

  useEffect(() => {
    if (state.kind !== 'done') return;
    // A phone verified in connect mode can raise the daily ceiling too: re-read the credits so a daily-limit
    // wall clears, as the phones do.
    if (mode.kind === 'connect') void queryClient.invalidateQueries({ queryKey: CREDITS_BALANCE_QUERY_KEY });
    const timer = setTimeout(onClose, WHATSAPP_VERIFY_DONE_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [state.kind, onClose, mode.kind, queryClient]);

  // `{code: '{code}'}` hands back the localized TEMPLATE; the link substitutes the token itself.
  const link = whatsappVerifyStateLink(state, t('credits.verifyWhatsapp.prefill', { code: '{code}' }));
  const limit = mode.kind === 'credits' ? String(mode.offer.limit) : '';

  const close = () => {
    logModalActionBi({ page_name: page.page_name, modal_name: copy.bi.sheetSeen.replace(/-/g, '_'), action: 'close' });
    onClose();
  };

  const onOpen = () => {
    logPageClickBi({ ...page, button_name: copy.bi.clicks.open }, { additionalFields: { state: state.kind } });
    if (!link) {
      // Expired or failed: a fresh code. Opening WhatsApp waits for the next press — a window opened after
      // an await is a popup the browser blocks.
      mint();
      return;
    }
    dispatch({ type: 'opened' });
    if (isPhone) window.location.href = link;
    else window.open(link, '_blank', 'noopener');
  };

  const useNumber = () => {
    logPageClickBi({ ...page, button_name: copy.bi.clicks.useNumber });
    onUseNumber();
  };

  const statusLine =
    state.kind === 'waiting'
      ? t('credits.verifyWhatsapp.waiting')
      : state.kind === 'expired'
        ? t(isPhone ? 'credits.verifyWhatsapp.expired' : 'credits.verifyWhatsapp.expiredDesktop')
        : state.kind === 'error'
          ? t('credits.verifyWhatsapp.error')
          : null;

  return (
    <Modal
      open
      onClose={close}
      width={440}
      maxHeightPx={650}
      scrollBody
      title={t(copy.titleKey)}
      labelledBy="verify-whatsapp-title"
      footer={
        state.kind === 'done' ? null : (
          <div className={styles.footer}>
            {isPhone ? (
              <Button fullWidth loading={state.kind === 'loading'} onClick={onOpen}>
                {t('credits.verifyWhatsapp.open')}
              </Button>
            ) : link ? (
              // A computer scans the QR above; WhatsApp Web is the second choice, for those who have it linked.
              <TextButton onClick={onOpen}>{t('credits.verifyWhatsapp.openHere')}</TextButton>
            ) : (
              <Button fullWidth loading={state.kind === 'loading'} onClick={onOpen}>
                {t('credits.verifyWhatsapp.newCode')}
              </Button>
            )}
            <TextButton onClick={useNumber}>{t('credits.verifyWhatsapp.useNumber')}</TextButton>
          </div>
        )
      }
    >
      <div ref={seenRef} className={styles.body}>
        {state.kind === 'done' ? (
          <Typography variant="p2" color="primary" component="p" role="status">
            {t(copy.doneKey, { limit })}
          </Typography>
        ) : (
          <>
            <Typography variant="p2" color="primary" component="p">
              {t(copy.bodyKey, { limit })}
            </Typography>
            {!isPhone && link && (
              <div className={styles.qr}>
                <Card variant="sunken" size="sm" disablePadding className={styles.qrPanel}>
                  {/* Black-on-white in both themes, as PhonePhotoDialog: an inverted code is refused by many scanners. */}
                  <QRCodeSVG
                    value={link}
                    size={QR_SIZE}
                    level="M"
                    marginSize={0}
                    bgColor="var(--ds-white)"
                    fgColor="var(--ds-black)"
                    title={t('credits.verifyWhatsapp.qr')}
                  />
                </Card>
                <Typography variant="p2" color="primary" component="p">
                  {t('credits.verifyWhatsapp.qr')}
                </Typography>
                <Typography variant="p3" color="secondary" component="p">
                  {t('credits.verifyWhatsapp.confirmNote')}
                </Typography>
              </div>
            )}
            {statusLine && (
              <Typography
                variant="p2"
                color={state.kind === 'waiting' ? 'secondary' : 'primary'}
                component="p"
                role="status"
                className={styles.status}
              >
                {state.kind === 'waiting' && <Spinner size={14} />}
                <span>{statusLine}</span>
              </Typography>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
